import { useMemo, useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { useT } from "../i18n";
import { MALE_QUESTIONS } from "../data/male_assessment";
import LanguageToggle from "../components/LanguageToggle";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Button } from "../components/ui/button";
import { ShieldCheck, Check } from "lucide-react";
import { useImageOverrides, resolveImage } from "../hooks/useImageOverrides";
import { useQuestionOverrides, resolveTitle, resolveHelper, resolveBridge, resolveLabel } from "../hooks/useQuestionOverrides";
import GoogleAuthButton from "../components/GoogleAuthButton";

const MaleProgress = ({ step, total }) => (
  <div className="fixed top-0 inset-x-0 z-40 bg-white/85 backdrop-blur-md border-b kt-line">
    <div className="max-w-md mx-auto px-6 py-3">
      <div className="flex items-center justify-between text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-2">
        <span data-testid="male-progress-label">{step}/{total} · 90-second check</span>
        <span>{Math.round((step / total) * 100)}%</span>
      </div>
      <div className="h-1 w-full bg-[#F3F3F3] rounded-full overflow-hidden">
        <div className="h-full bg-[#A8AD94] transition-all duration-300" style={{ width: `${(step / total) * 100}%` }} />
      </div>
    </div>
  </div>
);

const Card = ({ opt, qId, lang, selected, multi, onPick }) => {
  const overrides = useImageOverrides();
  const textOverrides = useQuestionOverrides();
  const imgSrc = resolveImage(overrides, `q_${qId}_${opt.id}`, opt.image);
  const label = resolveLabel(textOverrides, qId, opt.id, lang, opt.label);
  return (
  <motion.button
    variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0 } }}
    onClick={() => onPick(opt.id)}
    data-testid={`male-option-${opt.id}`}
    className={`answer-card group relative overflow-hidden rounded-2xl border border-[#E5E5E5] bg-white kt-shadow text-left ${selected ? "selected" : ""}`}
  >
    <div className="h-24 w-full overflow-hidden bg-[#F3F3F3]">
      <img src={imgSrc} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
    </div>
    <div className="p-3 flex items-center justify-between">
      <span className="text-sm text-[#383633] leading-snug">{label}</span>
      <span className={`h-4 w-4 shrink-0 flex items-center justify-center rounded-full border ${selected ? "bg-[#383633] border-[#383633]" : "border-[#383633]"}`}>
        {selected && multi && <Check className="h-3 w-3 text-white" />}
      </span>
    </div>
  </motion.button>
);
};

export default function MaleAssessment({ lang, setLang, languages, genderAnswer, ageBand, salonRef }) {
  const nav = useNavigate();
  const tr = useT(lang);
  const textOverrides = useQuestionOverrides();
  // Salon pre-quiz prefill — captured on the salon dashboard before launch.
  // Stored in localStorage so it survives popup tab isolation.
  const salonPrefill = (() => {
    if (typeof window === "undefined" || !salonRef) return null;
    try {
      const raw = localStorage.getItem("kt_salon_prequiz");
      if (!raw) return null;
      const p = JSON.parse(raw);
      if (p?.captured_at) {
        const ageMs = Date.now() - new Date(p.captured_at).getTime();
        if (Number.isFinite(ageMs) && ageMs > 30 * 60 * 1000) {
          try { localStorage.removeItem("kt_salon_prequiz"); } catch {}
          return null;
        }
      }
      return (p && p.first_name && p.email && p.consent) ? p : null;
    } catch { return null; }
  })();
  // Auto-save & resume: persist to localStorage so male-flow clients can
  // leave/refresh/come back and continue where they left off. 30-day TTL.
  const QUIZ_KEY = "kt_quiz_state_male_v1";
  const QUIZ_TTL_MS = 30 * 24 * 60 * 60 * 1000;
  const restored = (() => {
    if (typeof window === "undefined") return null;
    try {
      const raw = localStorage.getItem(QUIZ_KEY);
      if (!raw) return null;
      const p = JSON.parse(raw);
      if (!p || typeof p !== "object") return null;
      if (p.saved_at && Date.now() - p.saved_at > QUIZ_TTL_MS) {
        localStorage.removeItem(QUIZ_KEY);
        return null;
      }
      return p;
    } catch { return null; }
  })();
  const [answers, setAnswers] = useState(restored?.answers ?? { gender_for_visual: genderAnswer || "man" });
  const [step, setStep] = useState(restored?.step ?? 0);
  const [emailStep, setEmailStep] = useState(restored?.emailStep ?? false);
  const [name, setName] = useState(restored?.name ?? (salonPrefill?.first_name || ""));
  const [email, setEmail] = useState(restored?.email ?? (salonPrefill?.email || ""));
  const [consent, setConsent] = useState(restored?.consent ?? !!salonPrefill?.consent);
  const [submitting, setSubmitting] = useState(false);

  // Persist quiz state on every relevant change so it survives refresh/close/
  // OAuth round-trip. Cleared on successful submission.
  useEffect(() => {
    try {
      localStorage.setItem(QUIZ_KEY, JSON.stringify({
        answers, step, emailStep, name, email, consent, saved_at: Date.now(),
      }));
    } catch {}
  }, [answers, step, emailStep, name, email, consent]);

  // Filter conditional questions dynamically based on current answers
  const flow = useMemo(() =>
    MALE_QUESTIONS.filter((q) => !q.condition || q.condition(answers)),
    [answers]
  );
  const q = flow[step];
  const total = flow.length;

  const pickSingle = (optId) => {
    const next = { ...answers, [q.id]: optId };
    setAnswers(next);
    setTimeout(() => advance(next), 240);
  };
  const pickMulti = (optId) => {
    const current = answers[q.id] || [];
    let next;
    if (optId === "none") next = ["none"];
    else next = current.includes(optId) ? current.filter((x) => x !== optId) : [...current.filter((x) => x !== "none"), optId];
    setAnswers({ ...answers, [q.id]: next });
  };
  const commitMulti = () => {
    if (!(answers[q.id] || []).length) return;
    advance(answers);
  };
  const submitPrefilledMale = async (finalAnswers) => {
    if (!salonPrefill) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/assessments`, {
        first_name: salonPrefill.first_name,
        email: salonPrefill.email,
        marketing_consent: false,
        marketing_wording_version: "v1",
        language: lang,
        flow: "male",
        age_band: ageBand || "",
        salon_ref: salonRef || "",
        answers: finalAnswers,
      });
      try { localStorage.removeItem("kt_salon_prequiz"); } catch {}
      try { localStorage.removeItem(QUIZ_KEY); } catch {}
      nav(`/results/${res.data.result_id}`);
    } catch {
      toast.error(lang === "it" ? "Errore nell'invio. Riprova." : "Something went wrong.");
      setSubmitting(false);
    }
  };

  const advance = (a) => {
    const newFlow = MALE_QUESTIONS.filter((qx) => !qx.condition || qx.condition(a));
    if (step < newFlow.length - 1) setStep(step + 1);
    else if (salonPrefill) submitPrefilledMale(a);
    else setEmailStep(true);
  };
  const goBack = () => {
    if (emailStep) { setEmailStep(false); return; }
    if (step > 0) setStep(step - 1);
  };

  const submit = async () => {
    if (!name.trim() || !email.trim()) return toast.error(lang === "it" ? "Nome ed email obbligatori" : "Name and email are required");
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/assessments`, {
        first_name: name.trim(),
        email: email.trim(),
        marketing_consent: consent,
        marketing_wording_version: "v1",
        language: lang,
        flow: "male",
        age_band: ageBand || "",
        salon_ref: salonRef || "",
        answers,
      });
      try { localStorage.removeItem(QUIZ_KEY); } catch {}
      nav(`/results/${res.data.result_id}`);
    } catch {
      toast.error(lang === "it" ? "Errore nell'invio. Riprova." : "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white relative" data-testid="male-assessment">
      <MaleProgress step={emailStep ? total : step + 1} total={total} />
      <div className="fixed top-3 right-4 z-40">
        <LanguageToggle lang={lang} setLang={setLang} languages={languages} />
      </div>

      <div className="max-w-md mx-auto px-6 pt-20 pb-40">
        {!emailStep ? (
          <>
            <div className="text-[10px] kt-tracking-wider uppercase text-[#A8AD94] mb-2">
              KT Milano · {lang === "it" ? "Check Cuoio & Capelli 90-secondi" : "90-Second Scalp & Hair Check"}
            </div>
            {step === 0 && (
              <p className="text-[#6F6B66] mb-6 text-sm leading-relaxed">
                {lang === "it"
                  ? "Comprendi le tue abitudini di cuoio, la densità visibile e la routine quotidiana ideale."
                  : "Understand your scalp habits, visible density and ideal daily routine."}
              </p>
            )}
            <AnimatePresence mode="wait">
              <motion.div
                key={q.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.4 }}
                data-testid={`male-q-${q.id}`}
              >
                {q.bridge && (
                  <div className="mb-6 pl-4 border-l-2 border-[#383633] text-sm sm:text-base italic text-[#383633] max-w-lg" data-testid={`bridge-${q.id}`}>
                    {resolveBridge(textOverrides, q.id, lang, q.bridge)}
                  </div>
                )}
                <h2 className="text-3xl sm:text-4xl leading-tight text-[#383633] tracking-tight mb-3 font-medium">
                  {resolveTitle(textOverrides, q.id, lang, q.title)}
                </h2>
                {q.helper && (
                  <p className="text-sm italic text-[#6F6B66] mb-6 max-w-lg" data-testid={`male-helper-${q.id}`}>{resolveHelper(textOverrides, q.id, lang, q.helper)}</p>
                )}
                <motion.div
                  variants={{ show: { transition: { staggerChildren: 0.05 } } }}
                  initial="hidden"
                  animate="show"
                  className="grid grid-cols-2 gap-3"
                >
                  {q.options.map((opt) => {
                    const val = answers[q.id];
                    const selected = q.multi ? (val || []).includes(opt.id) : val === opt.id;
                    return (
                      <Card
                        key={opt.id}
                        opt={opt}
                        qId={q.id}
                        lang={lang}
                        selected={selected}
                        multi={!!q.multi}
                        onPick={q.multi ? pickMulti : pickSingle}
                      />
                    );
                  })}
                </motion.div>

                {q.multi && (
                  <Button
                    onClick={commitMulti}
                    disabled={!(answers[q.id] || []).length}
                    data-testid="male-multi-continue"
                    className="mt-6 w-full h-12 rounded-full bg-[#383633] hover:bg-[#4A4238] text-white text-xs kt-tracking uppercase"
                  >
                    {tr.continue}
                  </Button>
                )}
              </motion.div>
            </AnimatePresence>

            {(step > 0 || (q.multi && (answers[q.id] || []).length)) && (
              <div className="mt-8">
                <button onClick={goBack} data-testid="male-back-btn"
                  className="text-xs kt-tracking uppercase text-[#6F6B66] hover:text-[#383633]">← {tr.back}</button>
              </div>
            )}
          </>
        ) : (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} data-testid="email-form">
            <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-4">— {tr.email_kicker}</div>
            <h2 className="font-serif text-4xl leading-tight text-[#383633] tracking-tight mb-4">{tr.email_title}</h2>
            <p className="text-[#6F6B66] mb-8">{tr.email_sub}</p>

            {/* Emergent-managed Google sign-in — prefill only. */}
            <div className="mb-6" data-testid="email-form-google">
              <GoogleAuthButton
                lang={lang}
                variant="light"
                testid="male-assessment-google-btn"
                onSuccess={({ email: gEmail, name: gName }) => {
                  if (gEmail) setEmail(gEmail);
                  if (gName) setName(gName.split(" ")[0] || gName);
                  toast.success(
                    lang === "it"
                      ? "Account Google collegato — spunta il consenso e scopri i risultati."
                      : "Google account linked — tick consent and unlock your results."
                  );
                }}
              />
              <div className="mt-4 flex items-center gap-3 text-[10px] kt-tracking-wider uppercase text-[#8A857E]">
                <div className="flex-1 h-px bg-[#ECE7DF]" />
                <span>{lang === "it" ? "oppure inserisci l'email" : "or use email"}</span>
                <div className="flex-1 h-px bg-[#ECE7DF]" />
              </div>
            </div>

            <div className="space-y-5">
              <div>
                <Label className="text-xs kt-tracking uppercase text-[#6F6B66]">{tr.first_name}</Label>
                <Input data-testid="input-name" value={name} onChange={(e) => setName(e.target.value)}
                  className="mt-2 border-[#ECE7DF] bg-white rounded-xl h-12 focus-visible:ring-[#383633]" />
              </div>
              <div>
                <Label className="text-xs kt-tracking uppercase text-[#6F6B66]">{tr.email}</Label>
                <Input type="email" data-testid="input-email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="mt-2 border-[#ECE7DF] bg-white rounded-xl h-12 focus-visible:ring-[#383633]" />
                <p className="mt-2 text-[11px] text-[#6F6B66] flex items-start gap-1.5">
                  <ShieldCheck className="h-3 w-3 mt-0.5 shrink-0" /> {tr.privacy_short}
                </p>
              </div>
              <label className="flex items-start gap-3 cursor-pointer" data-testid="consent-row">
                <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)}
                  className="border-[#6F6B66] mt-1 data-[state=checked]:bg-[#383633] data-[state=checked]:border-[#383633]" data-testid="input-consent" />
                <span className="text-xs text-[#6F6B66] leading-relaxed">{tr.consent}</span>
              </label>
              <Button onClick={submit} disabled={submitting} data-testid="submit-assessment"
                className="w-full h-14 rounded-full bg-[#383633] hover:bg-[#4A4238] text-white text-xs kt-tracking uppercase">
                {submitting ? tr.submitting : tr.submit}
              </Button>
            </div>
            <div className="mt-8">
              <button onClick={goBack} data-testid="male-back-btn"
                className="text-xs kt-tracking uppercase text-[#6F6B66] hover:text-[#383633]">← {tr.back}</button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
